"use client";

import { useEffect, useState } from "react";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
type Metric = {
  prefix: string;
  value: number;
  suffix: string;
  display: string;
  label: string;
  descriptor: string;
};
const metrics: Metric[] = [{
  prefix: "",
  value: 100,
  suffix: "%",
  display: "100%",
  label: "AUTOMATION UPTIME",
  descriptor: "Reliable 24/7 engagement for your clients."
}, {
  prefix: "",
  value: 10,
  suffix: "yrs",
  display: "10yrs",
  label: "VETERAN LEADERSHIP",
  descriptor: "Disciplined, father-son operational excellence."
}, {
  prefix: "",
  value: 500,
  suffix: "+",
  display: "500+",
  label: "CALLS CAPTURED",
  descriptor: "Turning missed opportunities into booked revenue."
}, {
  prefix: "",
  value: 24,
  suffix: "/7",
  display: "24/7",
  label: "CLIENT BOOKING",
  descriptor: "Always active, always ready to serve."
}];
function CountUp({
  target,
  display,
  start,
  duration = 1200
}: {
  target: number;
  display: string;
  start: boolean;
  duration?: number;
}) {
  const [current, setCurrent] = useState(0);
  useEffect(() => {
    if (!start) return;
    let raf: number;
    const startTime = performance.now();
    const tick = (now: number) => {
      const elapsed = now - startTime;
      const progress = Math.min(elapsed / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 3);
      setCurrent(target * eased);
      if (progress < 1) {
        raf = requestAnimationFrame(tick);
      } else {
        setCurrent(target);
      }
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [start, target, duration]);
  const formatValue = () => {
    if (display.includes("%")) {
      return current.toFixed(0) + "%";
    }
    if (display.includes("yrs")) {
      return Math.round(current) + "yrs";
    }
    if (display.includes(",")) {
      return Math.round(current).toLocaleString() + "+";
    }
    if (display.includes("/")) {
      return Math.round(current) + "/7";
    }
    return Math.round(current).toString();
  };
  return <>{formatValue()}</>;
}
export default function MissionImpactMetrics() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll<HTMLDivElement>({
    once: true,
    threshold: 0.25
  });
  const positions = ["md:col-start-1 md:row-start-1 md:translate-y-0", "md:col-start-2 md:row-start-1 md:translate-y-[60px]", "md:col-start-1 md:row-start-2 md:translate-y-[40px]", "md:col-start-2 md:row-start-2 md:translate-y-[100px]"];
  return <section data-section-id="4575" id="mission-impact-metrics" ref={ref} className="relative bg-background text-foreground py-40 overflow-hidden">
      <div className="mx-auto max-w-7xl px-6 md:px-10 lg:px-16">
        {}
        <div className={`text-center mb-16 transition-all duration-1000 ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"}`}>
          
        </div>

        {}
        <div className="grid grid-cols-12 mb-24 md:mb-32">
          <h2 className={`col-span-12 text-center font-default font-bold text-4xl sm:text-5xl md:text-6xl lg:text-7xl leading-[1.05] tracking-tighter text-foreground transition-all duration-1000 delay-150 ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>Mission metrics that<br /><em className="italic font-normal text-primary" data-ast-id="f:/src/pages/Home/sections/MissionImpactMetrics.tsx|sig:vusxbs" data-ast-start="3338" data-ast-end="3439">drive your business.</em></h2>
        </div>

        {}
        <div className="relative">
          {}
          

          <div className="grid grid-cols-1 md:grid-cols-2 gap-y-20 md:gap-y-24 md:gap-x-12 lg:gap-x-20 relative">
            {metrics.map((metric, i) => <div data-index={i} key={i} className={`${positions[i]} transition-all duration-1000 ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`} style={{
            transitionDelay: `${300 + i * 150}ms`
          }}>
                <div className={`flex flex-col ${i % 2 === 1 ? "md:items-end md:text-right" : "items-start"}`}>
                  {}
                  <div className="font-default font-bold leading-none tracking-tighter text-primary" style={{
                fontSize: "clamp(5rem, 12vw, 9rem)"
              }}>
                    <CountUp target={metric.value} display={metric.display} start={isVisible} />
                  </div>

                  {}
                  <div className="mt-6 mb-5 h-px w-16 bg-chart-3" aria-hidden="true" />

                  {}
                  <div className="font-mono text-[11px] tracking-[0.28em] uppercase mb-3 text-foreground">
                    {metric.label}
                  </div>

                  {}
                  <p className="font-default italic font-light text-base md:text-lg leading-relaxed max-w-xs text-muted-foreground">
                    {metric.descriptor}
                  </p>
                </div>
              </div>)}
          </div>
        </div>
      </div>
    </section>;
}